import React, { useState, useEffect, useCallback } from 'react';
import { Play, Pause, Square, Clock, Plus, Calendar, User, Timer } from 'lucide-react';
import { api } from '../../features/auth/authStore';
import { useTimer } from '../../contexts/TimerContext';
import toast from 'react-hot-toast';

interface Task {
  id: string;
  title: string;
  clientId?: string;
  status: string;
}

interface TimeEntry {
  id: string;
  taskId?: string;
  description: string;
  startTime: Date;
  endTime?: Date;
  duration: number;
  billable: boolean;
  status: string;
}

interface ActiveTimer {
  id: string;
  taskId?: string;
  taskTitle?: string;
  description: string;
  startTime: Date;
  duration: number;
  billable: boolean;
  status: string;
}

interface UniversalTimeTrackerProps {
  userRole: string;
  userId: string;
  availableTasks?: Task[];
  onTimeEntryCreated?: (entry: TimeEntry) => void;
  onTimerStarted?: (timer: ActiveTimer) => void;
  onTimerStopped?: (entry: TimeEntry) => void;
  className?: string;
}

export const UniversalTimeTracker: React.FC<UniversalTimeTrackerProps> = ({
  userRole,
  userId,
  availableTasks = [],
  onTimeEntryCreated,
  onTimerStarted,
  onTimerStopped,
  className = ''
}) => {
  const { activeTimer, elapsedTime, loading, startTimer: startTimerContext, stopTimer: stopTimerContext } = useTimer();
  const [selectedTask, setSelectedTask] = useState<string>('');
  const [description, setDescription] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualEntry, setManualEntry] = useState({
    taskId: '',
    description: '',
    duration: '',
    date: new Date().toISOString().split('T')[0],
    billable: true
  });

  const startTimer = async () => {
    if (!selectedTask && availableTasks.length > 0) {
      toast.error('Please select a task');
      return;
    }

    try {
      await startTimerContext(selectedTask || undefined, description || 'Timer session');

      if (onTimerStarted && activeTimer) {
        onTimerStarted(activeTimer);
      }

      // Reset form
      setSelectedTask('');
      setDescription('');
    } catch (error: any) {
      console.error('Error starting timer:', error);
    }
  };

  const stopTimer = async () => {
    if (!activeTimer) return;

    try {
      await stopTimerContext();

      if (onTimerStopped) {
        // We'll need to get the stopped entry data from the context or pass it differently
        // For now, we'll create a mock entry based on the active timer
        const stoppedEntry = {
          ...activeTimer,
          endTime: new Date(),
          duration: elapsedTime
        };
        onTimerStopped(stoppedEntry);
      }

      // Reset form
      setDescription('');
      setSelectedTask('');
    } catch (error: any) {
      console.error('Error stopping timer:', error);
    }
  };

  const addManualEntry = async () => {
    if (!manualEntry.duration) {
      toast.error('Please enter duration');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/time-entries', {
        taskId: manualEntry.taskId || undefined,
        description: manualEntry.description,
        startTime: new Date(`${manualEntry.date}T09:00:00`),
        duration: parseInt(manualEntry.duration),
        billable: manualEntry.billable
      });

      if (response.data.success) {
        const entry = response.data.data;
        toast.success('Time entry added');
        setManualEntry({
          taskId: '',
          description: '',
          duration: '',
          date: new Date().toISOString().split('T')[0],
          billable: true
        });
        setShowManualEntry(false);
        
        if (onTimeEntryCreated) {
          onTimeEntryCreated(entry);
        }
      }
    } catch (error: any) {
      console.error('Error adding manual entry:', error);
      toast.error('Failed to add time entry');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const getTaskTitle = (taskId: string, taskTitle?: string): string => {
    // First try to use the task title from the timer data
    if (taskTitle) {
      return taskTitle;
    }
    // Fallback to looking up in availableTasks
    const task = availableTasks.find(t => t.id === taskId);
    return task ? task.title : 'Unknown Task';
  };

  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Timer className="w-5 h-5 mr-2" />
          Time Tracker
        </h3>
        <button
          onClick={() => setShowManualEntry(!showManualEntry)}
          className="flex items-center px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
        >
          <Plus className="w-4 h-4 mr-1" />
          Manual Entry
        </button>
      </div>

      {/* Active Timer Display */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Clock className="w-5 h-5 text-gray-500 mr-2" />
            <div>
              <div className="text-2xl font-mono font-bold text-gray-900">
                {formatDuration(elapsedTime)}
              </div>
              {activeTimer && (
                <div className="text-sm text-gray-600">
                  {activeTimer.taskId ? getTaskTitle(activeTimer.taskId, activeTimer.taskTitle) : 'No task selected'}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex gap-2">
            {!activeTimer ? (
              <button
                onClick={startTimer}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <Play className="w-4 h-4 mr-2" />
                Start
              </button>
            ) : (
              <button
                onClick={stopTimer}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                <Square className="w-4 h-4 mr-2" />
                Stop
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Timer Controls */}
      {!activeTimer && (
        <div className="space-y-4 mb-6">
          {availableTasks.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Task (Optional)
              </label>
              <select
                value={selectedTask}
                onChange={(e) => setSelectedTask(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">No specific task</option>
                {availableTasks.map(task => (
                  <option key={task.id} value={task.id}>
                    {task.title}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description (Optional)
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What are you working on?"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      )}

      {/* Manual Entry Form */}
      {showManualEntry && (
        <div className="border-t pt-6 mt-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">Add Manual Time Entry</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {availableTasks.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Task</label>
                <select
                  value={manualEntry.taskId}
                  onChange={(e) => setManualEntry(prev => ({ ...prev, taskId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">No specific task</option>
                  {availableTasks.map(task => (
                    <option key={task.id} value={task.id}>
                      {task.title}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Duration (minutes)</label>
              <input
                type="number"
                value={manualEntry.duration}
                onChange={(e) => setManualEntry(prev => ({ ...prev, duration: e.target.value }))}
                placeholder="60"
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
              <input
                type="date"
                value={manualEntry.date}
                onChange={(e) => setManualEntry(prev => ({ ...prev, date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="billable"
                checked={manualEntry.billable}
                onChange={(e) => setManualEntry(prev => ({ ...prev, billable: e.target.checked }))}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="billable" className="ml-2 block text-sm text-gray-700">
                Billable
              </label>
            </div>
          </div>
          
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={manualEntry.description}
              onChange={(e) => setManualEntry(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe the work performed..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => setShowManualEntry(false)}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={addManualEntry}
              disabled={loading || !manualEntry.duration}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Add Entry
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
